import { CalendarMonth, ChevronRight } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import { useYaMetrika } from '@/entities/yaMetrika';
import {
  CalDavSettingsModal,
  CalDavStatusIndicator,
  CalDavStoreProvider,
  useCalDavStore,
} from '@/features/caldav';
import { AntSwitch } from '@/features/settings/dialog/ui';
import { viewSettingsStore } from '@/features/viewSettings';
import { AnalyticsEvent } from '@/types/yametrika';

const IntegrationsPageContent: React.FC = observer(() => {
  const store = useCalDavStore();
  const { sendEvent } = useYaMetrika();
  const [calDavModalOpen, setCalDavModalOpen] = useState(false);
  const { addTimeWithQuickButtons, setAddTimeWithQuickButtons } = viewSettingsStore;

  const handleAddTimeWithQuickButtonsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddTimeWithQuickButtons(e.target.checked);
    sendEvent(AnalyticsEvent.settingsChangeTimeButtonsMode);
  };

  useEffect(() => {
    store.fetchStatus();
  }, [store]);

  const handleOpenCalDavModal = useCallback(() => {
    setCalDavModalOpen(true);
  }, []);

  const handleCloseCalDavModal = useCallback(() => {
    setCalDavModalOpen(false);
    // Refresh status after closing modal
    store.fetchStatus();
  }, [store]);

  if (store.isStatusLoading && !store.status) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const getCalDavDescription = () => {
    if (store.integrationStatus === 'not_configured') {
      return 'Не настроено';
    }

    if (store.integrationStatus === 'active') {
      const count = store.status?.selectedCalendarsCount ?? 0;

      return `Синхронизация ${count} ${getCalendarsWord(count)}`;
    }

    return store.status?.message ?? 'Требуется настройка';
  };

  return (
    <>
      <Typography variant="h5" component="h1" gutterBottom>
        Настройки
      </Typography>

      <Paper variant="outlined">
        <List disablePadding>
          <ListItem disablePadding divider>
            <ListItemButton onClick={handleOpenCalDavModal}>
              <ListItemIcon>
                <CalendarMonth />
              </ListItemIcon>
              <ListItemText primary="CalDav" secondary={getCalDavDescription()} />
              <Box display="flex" alignItems="center" gap={1}>
                <CalDavStatusIndicator
                  status={store.integrationStatus}
                  message={store.status?.message}
                />
                <ChevronRight color="action" />
              </Box>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Прибавлять время по кнопкам"
              secondary={
                <>
                  Включено: кнопки 1ч, 2ч и т.д. добавляют время к текущему.
                  <br />
                  Выключено: подставляют значение с кнопки
                </>
              }
            />
            <AntSwitch
              checked={addTimeWithQuickButtons}
              onChange={handleAddTimeWithQuickButtonsChange}
            />
          </ListItem>
        </List>
      </Paper>

      <CalDavSettingsModal open={calDavModalOpen} onClose={handleCloseCalDavModal} />
    </>
  );
});

function getCalendarsWord(count: number): string {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'календарей';
  }

  if (lastDigit === 1) {
    return 'календаря';
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'календарей';
  }

  return 'календарей';
}

export const IntegrationsPage: React.FC = () => {
  return (
    <CalDavStoreProvider>
      <Box
        sx={{
          p: 3,
          maxWidth: 600,
          fontFamily: 'Montserrat, sans-serif',
          '& .MuiTypography-root': { fontFamily: 'inherit' },
          '& .MuiListItemText-primary': { fontFamily: 'inherit' },
          '& .MuiListItemText-secondary': { fontFamily: 'inherit' },
        }}
      >
        <IntegrationsPageContent />
      </Box>
    </CalDavStoreProvider>
  );
};
